import apiService from '../services/api';

/**
 * A utility to handle API calls with localStorage fallback
 * This allows the application to work both with the backend API and in offline mode
 */
const apiWithFallback = {
  /**
   * Get data from API with localStorage fallback
   * @param {string} entity - Entity name (products, customers, etc.)
   * @param {Function} apiCall - API call function from apiService
   * @param {string} localStorageKey - localStorage key (defaults to stockpro_entityName)
   * @returns {Promise} - Promise that resolves with the data
   */
  getData: async (entity, apiCall, localStorageKey = null) => {
    try {
      // Try to get data from API
      const response = await apiCall();
      const data = response.data.data;
      
      // Save to localStorage for offline use
      localStorage.setItem(
        localStorageKey || `stockpro_${entity}`, 
        JSON.stringify(data)
      );
      
      return data;
    } catch (error) {
      console.warn(`API error for ${entity}, falling back to localStorage:`, error);
      
      // Fallback to localStorage
      try {
        const localData = localStorage.getItem(localStorageKey || `stockpro_${entity}`);
        if (localData) {
          return JSON.parse(localData);
        }
      } catch (localError) {
        console.error(`Error reading ${entity} from localStorage:`, localError);
      }
      
      // If all fails, return empty array
      return [];
    }
  },
  
  /**
   * Create data via API with localStorage fallback
   * @param {string} entity - Entity name (products, customers, etc.)
   * @param {Function} apiCall - API call function from apiService
   * @param {Object} data - Data to create
   * @param {string} localStorageKey - localStorage key (defaults to stockpro_entityName)
   * @returns {Promise} - Promise that resolves with the created data
   */
  createData: async (entity, apiCall, data, localStorageKey = null) => {
    try {
      // Try to create via API
      const response = await apiCall(data);
      const createdItem = response.data.data;
      
      // Update localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        let items = existingData ? JSON.parse(existingData) : [];
        items.push(createdItem);
        localStorage.setItem(key, JSON.stringify(items));
      } catch (localError) {
        console.error(`Error updating localStorage for ${entity}:`, localError);
      }
      
      return createdItem;
    } catch (error) {
      console.warn(`API error creating ${entity}, saving to localStorage only:`, error);
      
      // Fallback to localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        let items = existingData ? JSON.parse(existingData) : [];
        
        // Generate a local ID if none provided
        if (!data.id) {
          data.id = `local-${entity}-${Date.now()}`;
        }
        
        // Add timestamps
        data.createdAt = new Date().toISOString();
        data.updatedAt = new Date().toISOString();
        
        items.push(data);
        localStorage.setItem(key, JSON.stringify(items));
        
        return data;
      } catch (localError) {
        console.error(`Error saving ${entity} to localStorage:`, localError);
        throw localError;
      }
    }
  },
  
  /**
   * Update data via API with localStorage fallback
   * @param {string} entity - Entity name (products, customers, etc.)
   * @param {Function} apiCall - API call function from apiService
   * @param {string} id - ID of the item to update
   * @param {Object} data - Data to update
   * @param {string} localStorageKey - localStorage key (defaults to stockpro_entityName)
   * @returns {Promise} - Promise that resolves with the updated data
   */
  updateData: async (entity, apiCall, id, data, localStorageKey = null) => {
    try {
      // Try to update via API
      const response = await apiCall(id, data);
      const updatedItem = response.data.data;
      
      // Update localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        if (existingData) {
          let items = JSON.parse(existingData);
          const index = items.findIndex(item => item.id === id);
          if (index !== -1) {
            items[index] = updatedItem;
            localStorage.setItem(key, JSON.stringify(items));
          }
        }
      } catch (localError) {
        console.error(`Error updating localStorage for ${entity}:`, localError);
      }
      
      return updatedItem;
    } catch (error) {
      console.warn(`API error updating ${entity}, updating localStorage only:`, error);
      
      // Fallback to localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        if (existingData) {
          let items = JSON.parse(existingData);
          const index = items.findIndex(item => item.id === id);
          
          if (index !== -1) {
            // Update the item
            data.updatedAt = new Date().toISOString();
            items[index] = { ...items[index], ...data };
            localStorage.setItem(key, JSON.stringify(items));
            return items[index];
          } else {
            throw new Error(`Item with id ${id} not found in localStorage`);
          }
        } else {
          throw new Error(`No ${entity} found in localStorage`);
        }
      } catch (localError) {
        console.error(`Error updating ${entity} in localStorage:`, localError);
        throw localError;
      }
    }
  },
  
  /**
   * Delete data via API with localStorage fallback
   * @param {string} entity - Entity name (products, customers, etc.)
   * @param {Function} apiCall - API call function from apiService
   * @param {string} id - ID of the item to delete
   * @param {string} localStorageKey - localStorage key (defaults to stockpro_entityName)
   * @returns {Promise} - Promise that resolves with the deleted item ID
   */
  deleteData: async (entity, apiCall, id, localStorageKey = null) => {
    try {
      // Try to delete via API
      await apiCall(id);
      
      // Update localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        if (existingData) {
          let items = JSON.parse(existingData);
          const filteredItems = items.filter(item => item.id !== id);
          localStorage.setItem(key, JSON.stringify(filteredItems));
        }
      } catch (localError) {
        console.error(`Error updating localStorage for ${entity}:`, localError);
      }
      
      return { id, deleted: true };
    } catch (error) {
      console.warn(`API error deleting ${entity}, updating localStorage only:`, error);
      
      // Fallback to localStorage
      try {
        const key = localStorageKey || `stockpro_${entity}`;
        const existingData = localStorage.getItem(key);
        if (existingData) {
          let items = JSON.parse(existingData);
          const filteredItems = items.filter(item => item.id !== id);
          localStorage.setItem(key, JSON.stringify(filteredItems));
          return { id, deleted: true };
        } else {
          throw new Error(`No ${entity} found in localStorage`);
        }
      } catch (localError) {
        console.error(`Error deleting ${entity} from localStorage:`, localError);
        throw localError;
      }
    }
  }
};

export default apiWithFallback;